import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Av, EmptyState } from '../ui.jsx';
import { ContactProfile } from './ContactProfile.jsx';
import { CsvImportModal as CrmCsvImportModal } from '../widgets/CsvImportModal.jsx';

const STAGES = ['New Lead','Contacted','Qualified','Estimate Sent','Booked','In Progress','Invoiced','Paid','Lost'];
const SCOL = { 'New Lead': G.cyan, 'Contacted': G.blue, 'Qualified': G.purple, 'Estimate Sent': G.yellow, 'Booked': G.green, 'In Progress': G.orange, 'Invoiced': G.pink, 'Paid': G.green, 'Lost': G.red };

export function Contacts({ toast }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all'); // platinum/gold/silver/bronze/all
  const [tagFilter, setTagFilter] = useState('all');
  const [riskOnly, setRiskOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => api('/api/contacts').then(setContacts).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  const loadTags = () => api('/api/contacts/tags').then(setTags).catch(()=>{});
  useEffect(() => { load(); loadTags(); }, []);
  useEffect(() => {
    const onFocus = () => { load(); loadTags(); };
    const onContactUpdated = () => { load(); loadTags(); };
    window.addEventListener('tradeops:tab-focus', onFocus);
    window.addEventListener('tradeops:contact-updated', onContactUpdated);
    return () => {
      window.removeEventListener('tradeops:tab-focus', onFocus);
      window.removeEventListener('tradeops:contact-updated', onContactUpdated);
    };
  }, []);

  const filtered = contacts
    .filter(c => stageFilter === 'all' || c.stage === stageFilter)
    .filter(c => tierFilter === 'all' || c.ltvTier === tierFilter)
    .filter(c => tagFilter === 'all' || (c.tags||[]).includes(tagFilter))
    .filter(c => !riskOnly || (c.churnRiskScore || 0) >= 70)
    .filter(c => !search || `${c.firstName} ${c.lastName} ${c.phone} ${c.email} ${(c.tags||[]).join(' ')}`.toLowerCase().includes(search.toLowerCase()))
    // Sort by churn risk × LTV when filtering to risk; else default order
    .sort((a, b) => riskOnly ? ((b.churnRiskScore||0)*(b.ltvScore||1) - (a.churnRiskScore||0)*(a.ltvScore||1)) : 0);

  const remove = async id => {
    const target = contacts.find(c => c.id === id);
    // Optimistic remove + undo toast — soft-delete server-side
    setContacts(contacts.filter(c=>c.id!==id));
    try {
      await api(`/api/contacts/${id}`, { method:'DELETE' });
      toast(`Removed ${target?.firstName||'contact'}`, 'success', {
        action: 'Undo',
        durationMs: 8000,
        onAction: async () => {
          try { await api(`/api/contacts/${id}/restore`, { method:'POST' }); load(); toast('Restored'); }
          catch(e) { toast(e.message,'error'); }
        },
      });
    } catch(e) {
      toast(e.message,'error');
      load(); // rollback
    }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Contacts <span style={{ color:G.textMuted, fontWeight:500, fontSize:18 }}>({filtered.length})</span></h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn v="ghost" onClick={()=>window.location.href='/api/contacts/export.csv'}>↓ Export CSV</Btn>
        <Btn onClick={()=>setCsvImportOpen(true)}>📥 Import CSV</Btn>
        <Btn v="primary" onClick={()=>setEditing({ stage:'New Lead', source:'Manual', state:'FL' })}>+ New Contact</Btn>
      </div>
    </div>


    <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
      <Input placeholder="Search name, phone, email..." value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:340 }}/>
      <Select value={stageFilter} onChange={e=>setStageFilter(e.target.value)} style={{ maxWidth:180 }}>
        <option value="all">All Stages</option>
        {STAGES.map(s => <option key={s}>{s}</option>)}
      </Select>
      <Select value={tierFilter} onChange={e=>setTierFilter(e.target.value)} style={{ maxWidth:160 }}>
        <option value="all">All Tiers</option>
        <option value="platinum">💎 Platinum ($10k+)</option>
        <option value="gold">🥇 Gold ($3k+)</option>
        <option value="silver">🥈 Silver ($500+)</option>
        <option value="bronze">🥉 Bronze</option>
      </Select>
      <Select value={tagFilter} onChange={e=>setTagFilter(e.target.value)} style={{ maxWidth:180 }}>
        <option value="all">All Tags</option>
        {tags.map(({tag, count}) => <option key={tag} value={tag}>🏷 {tag} ({count})</option>)}
      </Select>
      <label style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 12px', background: riskOnly?G.red+'22':G.input, borderRadius:8, cursor:'pointer', border:`1px solid ${riskOnly?G.red+'66':G.cardBorder}`, fontSize:13 }}>
        <input type="checkbox" checked={riskOnly} onChange={e=>setRiskOnly(e.target.checked)}/>
        🚨 Churn risk only
      </label>
      <Btn sz="sm" v="ghost" onClick={async () => {
        if (!confirm('Reset churn risk for imported leads?\n\nNewly imported contacts have no activity history yet, so the engine flags them all as "high risk" — this wipes those false positives so the scores can rebuild from real activity.')) return;
        try {
          const r = await api('/api/contacts/reset-churn-risk', { method:'POST', body:{ scope:'imported' }});
          toast(`✓ Reset churn risk on ${r.reset} imported lead${r.reset===1?'':'s'}`);
          load();
        } catch(e) { toast(e.message, 'error'); }
      }}>♻️ Reset churn risk on imports</Btn>
    </div>

    {selected.size > 0 && (
      <BulkBar count={selected.size} busy={bulkBusy} onClear={()=>setSelected(new Set())} onAction={async (action, value) => {
        setBulkBusy(true);
        try {
          const r = await api('/api/contacts/bulk', { method:'POST', body:{ ids:[...selected], action, value }});
          toast(`✓ ${r.affected} contact${r.affected===1?'':'s'} ${action.replace('_',' ')}${value?': '+value:''}`);
          setSelected(new Set()); load(); loadTags();
        } catch(e) { toast(e.message,'error'); }
        finally { setBulkBusy(false); }
      }}/>
    )}

    {loading ? <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
    : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          <th style={{ ...styles.th, width:34 }}>
            <input type="checkbox"
              checked={filtered.length > 0 && filtered.every(c => selected.has(c.id))}
              onChange={(e) => {
                if (e.target.checked) setSelected(new Set([...selected, ...filtered.map(c=>c.id)]));
                else setSelected(new Set([...selected].filter(id => !filtered.some(c=>c.id===id))));
              }}/>
          </th>
          {['Contact','Service','Value','Stage','Source','Notes','Last Contact',''].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>{filtered.map(c => (
          <tr key={c.id} onClick={()=>{
              // Opens the global in-app contact profile (overlay inside the shell).
              // The user can still click sidebar items to navigate away; the profile
              // auto-closes on tab change.
              try { window.dispatchEvent(new CustomEvent('tradeops:open-contact', { detail:{ id: c.id }})); }
              catch { setProfileId(c.id); }
            }} style={{ cursor:'pointer', background: selected.has(c.id) ? G.purpleDim+'44' : 'transparent' }}>
            <td style={styles.td} onClick={(e)=>e.stopPropagation()}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(c.id); else next.delete(c.id);
                setSelected(next);
              }}/>
            </td>
            <td style={styles.td}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <Av name={`${c.firstName} ${c.lastName||''}`}/>
                <div>
                  <div style={{ fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
                    {c.firstName} {c.lastName}
                    {c.ltvTier === 'platinum' && <span title={`LTV $${c.ltvScore}`}>💎</span>}
                    {c.ltvTier === 'gold' && <span title={`LTV $${c.ltvScore}`}>🥇</span>}
                    {(c.churnRiskScore || 0) >= 70 && <span title={`Churn risk ${c.churnRiskScore}%`} style={{ color:'#ef4444' }}>🚨</span>}
                  </div>
                  <div style={{ fontSize:12, color:G.textMuted }}>{c.phone} · {c.email}</div>
                  {(c.tags||[]).length > 0 && (
                    <div style={{ display:'flex', gap:4, marginTop:4, flexWrap:'wrap' }}>
                      {(c.tags||[]).slice(0,4).map(t => (
                        <span key={t} onClick={(e)=>{e.stopPropagation(); setTagFilter(t);}}
                          style={{ fontSize:10, padding:'1px 6px', borderRadius:4, background:G.purpleDim, color:G.purple, cursor:'pointer' }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td style={styles.td}>{c.service || '—'}</td>
            <td style={styles.td}>{fmtUsd(c.value || 0)}</td>
            <td style={styles.td}><Badge c={SCOL[c.stage]} bg={SCOL[c.stage]+'22'}>{c.stage}</Badge></td>
            <td style={styles.td}>{c.source}</td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted, maxWidth:220 }}>
              {c.notes ? <span title={c.notes} style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.notes}</span> : '—'}
            </td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{c.lastContact ? new Date(c.lastContact).toLocaleDateString() : '—'}</td>
            <td style={styles.td} onClick={e=>e.stopPropagation()}>
              <Btn sz="xs" v="danger" onClick={()=>remove(c.id)}>Delete</Btn>
            </td>
          </tr>
        ))}</tbody>
      </table>
      {filtered.length === 0 && (contacts.length === 0
        ? <EmptyState
            title="No contacts yet"
            body="Contacts are the heart of your CRM — every lead, customer, and job ties back here. Add your first contact to start sending estimates, scheduling jobs, and tracking conversations."
            ctaLabel="+ Add first contact"
            onCta={()=>setEditing({ stage:'New Lead', source:'Manual', state:'FL' })}
            secondaryLabel="Import from CSV"
            onSecondary={()=>setImporting(true)}
          />
        : <div style={{ padding:48, textAlign:'center', color:G.textMuted }}>No contacts match your filters.</div>
      )}
    </div>}

    <ContactModal contact={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Contact saved'); }} />
    <ContactProfile contactId={profileId} onClose={()=>setProfileId(null)} onChanged={load} toast={toast}/>
    {csvImportOpen && <CrmCsvImportModal onClose={()=>setCsvImportOpen(false)} onImported={()=>{ load(); }} toast={toast}/>}
  </div>;
}

function BulkBar({ count, busy, onClear, onAction }) {
  const [tag, setTag] = useState('');
  const [stage, setStage] = useState('');
  const [wfId, setWfId] = useState('');
  const [workflows, setWorkflows] = useState([]);
  useEffect(() => { api('/api/workflows').then(setWorkflows).catch(()=>setWorkflows([])); }, []);
  return <div style={{ display:'flex', flexDirection:'column', gap:8, padding:'12px 16px', background:G.purpleDim, border:`1px solid ${G.purple}66`, borderRadius:10, marginBottom:14 }}>
    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
      <strong style={{ color:G.purple }}>{count} selected</strong>
      <span style={{ color:G.textMuted, fontSize:13 }}>·</span>
      <Input value={tag} onChange={e=>setTag(e.target.value)} placeholder="Tag name" style={{ width:140 }}/>
      <Btn sz="sm" disabled={busy||!tag.trim()} onClick={()=>onAction('add_tag', tag.trim())}>+ Add tag</Btn>
      <Btn sz="sm" disabled={busy||!tag.trim()} v="ghost" onClick={()=>onAction('remove_tag', tag.trim())}>– Remove tag</Btn>
      <span style={{ color:G.textMuted, fontSize:13 }}>·</span>
      <Select value={stage} onChange={e=>setStage(e.target.value)} style={{ width:160 }}>
        <option value="">Set stage…</option>
        {STAGES.map(s => <option key={s}>{s}</option>)}
      </Select>
      <Btn sz="sm" disabled={busy||!stage} onClick={()=>{ onAction('set_stage', stage); setStage(''); }}>Apply stage</Btn>
      <Btn sz="sm" v="danger" disabled={busy} onClick={()=>{
        if (confirm(`Delete ${count} contacts? (You'll be able to undo individually from Activity Log.)`)) onAction('delete');
      }}>🗑 Delete all</Btn>
      <div style={{ flex:1 }}/>
      <Btn sz="sm" v="ghost" onClick={onClear}>Clear</Btn>
    </div>

    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', paddingTop:8, borderTop:`1px solid ${G.purple}33` }}>
      <span style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Campaign / AI</span>
      <Select value={wfId} onChange={e=>setWfId(e.target.value)} style={{ width:240 }}>
        <option value="">Pick a workflow / campaign…</option>
        {workflows.map(w => <option key={w.id} value={w.id}>{w.name} ({w.status})</option>)}
      </Select>
      <Btn sz="sm" v="primary" disabled={busy||!wfId} onClick={()=>{ onAction('enroll_workflow', wfId); }}>➕ Enroll</Btn>
      <Btn sz="sm" v="ghost" disabled={busy||!wfId} onClick={()=>{ onAction('unenroll_workflow', wfId); }}>− Remove from workflow</Btn>
      <span style={{ color:G.textMuted, fontSize:13 }}>·</span>
      <Btn sz="sm" onClick={()=>onAction('set_wf_paused', true)} disabled={busy}>⏸ Pause workflows</Btn>
      <Btn sz="sm" onClick={()=>onAction('set_wf_paused', false)} disabled={busy}>▶ Resume workflows</Btn>
      <span style={{ color:G.textMuted, fontSize:13 }}>·</span>
      <Btn sz="sm" onClick={()=>onAction('set_ai_active', false)} disabled={busy}>🔇 AI off</Btn>
      <Btn sz="sm" onClick={()=>onAction('set_ai_active', true)} disabled={busy}>🔊 AI on</Btn>
    </div>

    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', paddingTop:8, borderTop:`1px solid ${G.purple}33` }}>
      <span style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Do Not Disturb</span>
      <Btn sz="sm" v="danger" disabled={busy} onClick={()=>onAction('dnd_on', 'all')}>🚫 DND all channels</Btn>
      <Btn sz="sm" disabled={busy} onClick={()=>onAction('dnd_on', 'sms')}>📵 SMS</Btn>
      <Btn sz="sm" disabled={busy} onClick={()=>onAction('dnd_on', 'email')}>Email</Btn>
      <Btn sz="sm" disabled={busy} onClick={()=>onAction('dnd_on', 'voice')}>Voice</Btn>
      <Btn sz="sm" disabled={busy} onClick={()=>onAction('dnd_on', 'inbound')}>📥 Inbound (silently drop)</Btn>
      <span style={{ color:G.textMuted, fontSize:13 }}>·</span>
      <Btn sz="sm" v="green" disabled={busy} onClick={()=>onAction('dnd_off', 'all')}>✓ Lift DND (all)</Btn>
    </div>
  </div>;
}

function ContactModal({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(contact || {}); }, [contact]);
  if (!contact) return null;
  const set = k => e => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const isNew = !form.id;

  const save = async () => {
    try {
      if (isNew) await api('/api/contacts', { method:'POST', body: form });
      else await api(`/api/contacts/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 18px 0' }}>{isNew ? 'New Contact' : `${form.firstName} ${form.lastName||''}`}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="First Name *"><Input value={form.firstName||''} onChange={set('firstName')} /></Field>
      <Field label="Last Name"><Input value={form.lastName||''} onChange={set('lastName')} /></Field>
      <Field label="Phone *"><Input value={form.phone||''} onChange={set('phone')} /></Field>
      <Field label="Email"><Input value={form.email||''} onChange={set('email')} /></Field>
      <Field label="Address"><Input value={form.address||''} onChange={set('address')} /></Field>
      <Field label="City"><Input value={form.city||''} onChange={set('city')} /></Field>
      <Field label="Service"><Input value={form.service||''} onChange={set('service')} placeholder="e.g. AC Replacement"/></Field>
      <Field label="Estimated Value"><Input type="number" value={form.value||0} onChange={set('value')} /></Field>
      <Field label="Stage">
        <Select value={form.stage||'New Lead'} onChange={set('stage')}>
          {STAGES.map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Source * (required)">
        <Select value={form.source||''} onChange={set('source')} required>
          <option value="">— Pick a source —</option>
          {['Missed Call','Inbound Call','Website Form','Booking Widget','Google Ads','Google Local Service Ad','Meta / Facebook Ad','TikTok Ad','Yelp','Angi','Thumbtack','Nextdoor','Door Hanger','Direct Mail','Referral — Existing Customer','Referral — Other Trade','Walk-in / Sign','Repeat Customer','Other'].map(s => <option key={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Tags">
        <Input value={(form.tags||[]).join(', ')}
          onChange={(e)=>setForm({...form, tags: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)})}
          placeholder="VIP, Maintenance Plan, Snowbird (comma-separated)"/>
      </Field>
    </div>
    <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

const FIELD_OPTIONS = [
  { key:'firstName', label:'First name *' },
  { key:'lastName', label:'Last name' },
  { key:'phone', label:'Phone *' },
  { key:'email', label:'Email' },
  { key:'address', label:'Address' },
  { key:'city', label:'City' },
  { key:'state', label:'State' },
  { key:'zip', label:'ZIP' },
  { key:'service', label:'Service' },
  { key:'value', label:'Value ($)' },
  { key:'source', label:'Source' },
  { key:'stage', label:'Stage' },
  { key:'notes', label:'Notes' },
  { key:'', label:'— skip —' },
];

function CsvImportModal({ open, onClose, onDone }) {
  const [csvText, setCsvText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setCsvText(String(ev.target.result || '')); parse(String(ev.target.result || '')); };
    reader.readAsText(file);
  };

  const parse = (text) => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return setParsed({ headers: [], rows: [] });
    // Simple CSV parser — handles quoted fields with commas + escaped quotes
    const splitLine = (line) => {
      const out = []; let cur = ''; let q = false;
      for (let i=0;i<line.length;i++) {
        const c = line[i];
        if (c === '"') { if (q && line[i+1] === '"') { cur += '"'; i++; } else q = !q; }
        else if (c === ',' && !q) { out.push(cur); cur = ''; }
        else cur += c;
      }
      out.push(cur);
      return out;
    };
    const headers = splitLine(lines[0]).map(h => h.trim());
    const rows = lines.slice(1, 1001).map(l => splitLine(l)); // cap import at 1000 rows
    setParsed({ headers, rows });
    // Auto-detect mapping by header name
    const m = {};
    headers.forEach((h, i) => {
      const lower = h.toLowerCase().replace(/[\s_]/g,'');
      if (lower.includes('firstname')||lower==='first') m[i] = 'firstName';
      else if (lower.includes('lastname')||lower==='last') m[i] = 'lastName';
      else if (lower.includes('phone')||lower.includes('mobile')||lower.includes('cell')) m[i] = 'phone';
      else if (lower.includes('email')) m[i] = 'email';
      else if (lower==='address'||lower.includes('street')) m[i] = 'address';
      else if (lower==='city') m[i] = 'city';
      else if (lower==='state') m[i] = 'state';
      else if (lower==='zip'||lower==='postal') m[i] = 'zip';
      else if (lower.includes('service')) m[i] = 'service';
      else if (lower==='value'||lower==='amount') m[i] = 'value';
      else if (lower==='source') m[i] = 'source';
      else if (lower==='stage'||lower==='status') m[i] = 'stage';
      else if (lower==='notes'||lower==='comments') m[i] = 'notes';
    });
    setMapping(m);
  };

  const doImport = async () => {
    if (!parsed) return;
    const rows = parsed.rows.map(cells => {
      const obj = {};
      Object.entries(mapping).forEach(([colIdx, field]) => {
        if (field) obj[field] = cells[Number(colIdx)] || '';
      });
      return obj;
    }).filter(r => r.firstName && r.phone);

    if (!rows.length) return alert('No rows match — make sure firstName and phone columns are mapped');

    setBusy(true);
    try {
      const r = await api('/api/contacts/import', { method:'POST', body:{ rows } });
      setResult(r);
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  return <Modal open onClose={onClose} width={780}>
    <h2 style={{ margin:'0 0 6px' }}>Import Contacts from CSV</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:18 }}>
      Upload a CSV with one contact per row. The first row should be column headers. We auto-detect common field names and let you fix the mapping before importing.
    </div>

    {!parsed && <div style={{ ...styles.card, padding:30, textAlign:'center', border:`2px dashed ${G.cardBorder}` }}>
      <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ marginBottom:14 }}/>
      <div style={{ fontSize:12, color:G.textMuted }}>Common sources: ServiceTitan export, GHL export, Google Contacts, Jobber export.</div>
    </div>}

    {parsed && !result && <>
      <div style={{ ...styles.card, padding:14, marginBottom:14 }}>
        <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>
          Map columns ({parsed.headers.length} columns, {parsed.rows.length} rows)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, fontSize:13, maxHeight:260, overflowY:'auto' }}>
          {parsed.headers.map((h, i) => (
            <div key={i} style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <span style={{ fontSize:11, color:G.textMuted }}>{h || `Column ${i+1}`}</span>
              <Select value={mapping[i]||''} onChange={e=>setMapping({...mapping, [i]: e.target.value})}>
                {FIELD_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </Select>
              <span style={{ fontSize:10, color:G.textDim, fontStyle:'italic' }}>{(parsed.rows[0]?.[i]||'').slice(0,30)}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:12, color:G.textMuted }}>De-duped by phone. Existing contacts kept untouched.</span>
        <div style={{ display:'flex', gap:8 }}>
          <Btn onClick={()=>{ setParsed(null); setCsvText(''); }}>Pick another file</Btn>
          <Btn v="primary" onClick={doImport} disabled={busy}>{busy ? <Spinner sz={14}/> : `Import ${parsed.rows.length} rows`}</Btn>
        </div>
      </div>
    </>}

    {result && <div>
      <div style={{ ...styles.card, padding:20, textAlign:'center', background:G.greenLight, borderColor:G.green+'66', marginBottom:14 }}>
        <div style={{ fontSize:24, fontWeight:800, color:G.green }}>✓ {result.createdCount} contacts imported</div>
        {result.skippedCount > 0 && <div style={{ fontSize:13, color:G.textMuted, marginTop:6 }}>{result.skippedCount} skipped (duplicates or missing required fields)</div>}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <Btn v="primary" onClick={onDone}>Done</Btn>
      </div>
    </div>}
  </Modal>;
}
